import React, { useState } from 'react';
import { useWorkspaceParams } from '../../hooks/useWorkspaceParams';
import { useWallet } from '../../hooks/useWallet';
import { useInstanceStore } from '../../stores/UseInstanceStore';
import { trackEvent } from '../../hooks/useGoogleAnalytics';
import { OnboardingStep1 } from './OnboardingStep1';
import { OnboardingStep2 } from './OnboardingStep2';
import { OnboardingStep3 } from './OnboardingStep3';
import { OnboardingStep4 } from './OnboardingStep4';

function StepDots({ current, labels }) {
  return (
    <div className="flex items-center justify-center gap-3 w-full my-4">
      {labels.map((label, i) => {
        const n = i + 1;
        const isDone = n < current;
        const isActive = n === current;
        return (
          <React.Fragment key={n}>
            <div className="flex flex-col items-center">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm border-2 transition-colors ${
                isDone ? 'bg-cyan-600 border-cyan-600 text-white'
                  : isActive ? 'border-cyan-400 text-cyan-400'
                  : 'border-slate-600 text-slate-500'
              }`}>
                {isDone ? '✓' : n}
              </div>
              <span className={`text-xs mt-1 ${isActive ? 'font-bold text-white' : 'text-slate-400'}`}>
                {label}
              </span>
            </div>
            {i < labels.length - 1 && (
              <div className={`flex-1 h-px ${isDone ? 'bg-cyan-600' : 'bg-slate-700'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/**
 * 4-step onboarding wizard — modal overlay inside WorkspacePage.
 * Rendered when ?modal=onboarding is in the URL.
 */
export function OnboardingWizard({ portfolioId, portfolioName, config, onComplete }) {
  const { step, setModal, closeModal } = useWorkspaceParams();
  const { jwtToken } = useWallet();
  const { setOnboardingCompleted } = useInstanceStore();

  const currentStep = parseInt(step) || 1;

  // Wizard state carried between steps
  const [agentId, setAgentId] = useState(null);
  const [agentName, setAgentName] = useState('');
  const [bucket, setBucket] = useState('MOMENTUM');

  const goToStep = (n) => setModal('onboarding', n);

  const handleComplete = () => {
    setOnboardingCompleted(true);
    trackEvent('onboarding_complete', { portfolio_id: portfolioId });
    closeModal();
    if (onComplete) onComplete();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 sm:pt-20">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={closeModal} />

      {/* Modal card */}
      <div className="relative w-[560px] max-w-[95vw] bg-slate-900 border border-slate-700 rounded-xl shadow-2xl p-6 max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg sm:text-xl font-bold text-white">
            {currentStep === 4 ? 'running your first analysis…' : "let's get your first trade running"}
          </h2>
          <button onClick={closeModal} className="text-slate-400 hover:text-white text-sm p-1">✕</button>
        </div>

        {/* Step dots */}
        <StepDots current={currentStep} labels={['Meet', 'Strategy', 'Name', 'Analyze']} />

        {/* Step content */}
        {currentStep === 1 && (
          <OnboardingStep1
            config={config}
            portfolioId={portfolioId}
            jwtToken={jwtToken}
            onComplete={({ agentId: id, agentName: name }) => {
              setAgentId(id);
              setAgentName(name);
              goToStep(2);
            }}
          />
        )}
        {currentStep === 2 && agentId && (
          <OnboardingStep2
            config={config}
            agentId={agentId}
            jwtToken={jwtToken}
            onComplete={({ bucket: b }) => {
              setBucket(b);
              goToStep(3);
            }}
            onBack={() => goToStep(1)}
          />
        )}
        {currentStep === 3 && (
          <OnboardingStep3
            config={config}
            portfolioId={portfolioId}
            portfolioName={portfolioName || 'default'}
            jwtToken={jwtToken}
            onComplete={() => goToStep(4)}
            onBack={() => goToStep(2)}
          />
        )}
        {currentStep === 4 && (
          <OnboardingStep4
            config={config}
            portfolioId={portfolioId}
            agentName={agentName}
            bucket={bucket}
            jwtToken={jwtToken}
            onComplete={handleComplete}
          />
        )}
      </div>
    </div>
  );
}
